import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import {
  type WorkflowCase,
  WorkflowCaseStatus,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { UpdateWorkflowCaseRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import type { WorkflowCaseTag } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
  chakra,
} from '@enechain/ecloud-designsystem'
import { FaPenToSquareRegular } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { WorkflowCaseStatusBadge } from '~/components/common/WorkflowCaseStatusBadge'
import { WorkflowCaseTagChip } from '~/components/common/WorkflowCaseTagChip'
import { WorkflowCaseTagEditor } from '~/components/common/WorkflowCaseTagEditor'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useListWorkflowTagPool } from '~/hooks/workflowCase/useListWorkflowTagPool'
import { convertAreaLabel } from '~/models/area'
import { formatDateDisplay, formatDateTimeFull } from '~/models/dateTime'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { areaLabelOrDash } from '~/models/workflowCase'
import { StepProgressBar } from '~/pages/WorkflowCaseDetail/components/StepProgressBar'
import { useUpdateWorkflowCase } from '~/pages/WorkflowCaseDetail/hooks/useUpdateWorkflowCase'
import {
  WORKFLOW_CASE_BG_ABSENT_LABEL,
  WORKFLOW_CASE_NO_VALUE_LABEL,
  workflowCaseSectionLabelKey,
} from '~/pages/WorkflowCases/models/workflowCase'

type Props = {
  wfCase: WorkflowCase
  doneSubSteps: number
  totalSubSteps: number
  /** 完了済み等で変更不可のとき true (タグの追加・削除を disable する)。 */
  isReadonly?: boolean
}

/**
 * ケースヘッダーカード (design/demand/case-detail.html `.cd-header` 相当)。
 *
 * タイトル行 (計画断面・エリア・対象日) + フィールドグリッド (ワークフローID/区分/
 * 計画断面/エリア/BGコード/対象日/テンプレート/作成日時/ステータス/進捗) を表示する。
 *
 * 大ステップ単位の担当者表示は BFF response に該当フィールドが無いため実装しない
 * (backend 対応待ち gap、完了報告に記載)。
 */
export const CaseHeader = ({
  wfCase,
  doneSubSteps,
  totalSubSteps,
  isReadonly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const resolveDisplayName = useMemberDisplayName()
  // 終端 (削除・完了) の記録。 削除済みは completedAt を持つこともあるので status を先に見る
  const terminalRecord =
    wfCase.status === WorkflowCaseStatus.DELETED
      ? {
          at: wfCase.deletedAt,
          by: wfCase.deletedBy,
          atLabelKey: 'page:workflowCaseDetail.header.deletedAt',
          byLabelKey: 'page:workflowCaseDetail.header.deletedBy',
        }
      : wfCase.completedAt !== undefined
        ? {
            at: wfCase.completedAt,
            by: wfCase.completedBy,
            atLabelKey: 'page:workflowCaseDetail.header.completedAt',
            byLabelKey: 'page:workflowCaseDetail.header.completedBy',
          }
        : undefined
  const { mutateAsync: updateAsync, isPending: isUpdating } =
    useUpdateWorkflowCase(wfCase.id)
  // 組織全体のタグプール (spec AC-TAG-005): サジェスト表示 + 20 個上限事前
  // チェック (AC-TAG-006) の両方に使う (tag-pool-limit-and-suggest design)。
  const { data: tagPoolData } = useListWorkflowTagPool()
  const [tagsError, setTagsError] = useState<string | undefined>(undefined)
  // AC-TAG-009 (workflow-tag-delete-ui design Decision C): 「作成済のタグ」
  // ボックスの編集モードで削除予定にした label の集合。 org 横断削除は
  // 明示の確認 (下の ConfirmDialog) を経てからでないと送出しない。
  // 2026-08-28: 確認の起点だった「削除を保存」 ボタンは廃止し、 保存導線を
  // 「保存」 に一本化した (`handleSaveClick` が削除予定の有無で確認を挟む)。
  const [removedTagLabels, setRemovedTagLabels] = useState<string[]>([])
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false)
  // タグ編集モード (design `.cd-edit-btn` 相当、 2026-08-28 デザイナー指摘
  // 「編集用パーツが全部表示されている」)。 既定はヘッダーに付与タグを
  // 読み取り専用のチップで出すだけにして、 入力欄・色スウォッチ・作成済タグの
  // プールはこのモードのときだけ出す。
  const [isEditingTags, setIsEditingTags] = useState(false)
  /**
   * 実際に編集パーツを出すか。 `isEditingTags` 単独で判定しない。
   *
   * `isReadonly` は実行中に false → true へ変わる: 詳細は 30 秒 polling
   * (`index.tsx` の `refetchInterval`) で再取得され、 他ユーザーの完了・削除の
   * ほか、 自分がこの画面から削除する / 最終サブステップを完了して自動完了する
   * 経路でも detail が invalidate される。 編集モードを抜ける導線 (下の
   * TertiaryButton) は `!isReadonly` の中にあるので、 描画側でも閉じておかないと
   * 「disabled の入力欄・色スウォッチ・タグプールが residual に残り、
   * リロードするまで閉じられない」 状態になる (2026-08-28 レビュー指摘)。
   *
   * state を effect でリセットするのではなく描画時に導出する — prop から決まる
   * 表示を effect で state 同期すると余分な render が挟まり、 同じ不変条件の
   * SoT が state と描画条件の 2 つに割れる。
   */
  const isTagEditing = isEditingTags && !isReadonly

  /**
   * 編集モード中の下書き。 編集モードでない間は null。
   *
   * 編集中の追加・削除はここにだけ溜め、 サーバーへは「保存」 で 1 回だけ送る
   * (design `demand/case-detail.html` の編集 modal が
   * `__cdEditSave()` で確定するのと同じ形。 2026-08-28 デザイナー指示)。
   * 「キャンセル」 で捨てられる必要があるので、 下書きを持たずに即時保存する
   * 旧実装のままではラベルが実態と食い違う。
   */
  const [draftTags, setDraftTags] = useState<WorkflowCaseTag[] | null>(null)
  /**
   * 下書きを取った時点の `updated_at`。 楽観ロックの基準にこれを使う。
   *
   * `wfCase.updatedAt` を保存時に読むと **ロックが無効化される**: `wfCase` は
   * 30 秒 polling (`index.tsx` の `refetchInterval`) で更新されるため、 編集中に
   * 他ユーザーがタグを変えると `updatedAt` だけ最新化され、 送る `tags` は古い
   * 下書きのまま残る。 `tags` は full replace なので、 ロックは一致して通り
   * **相手の変更が無言で消える**。 即時保存だった頃は配列とロック基準が同じ
   * render の値だったのでこの穴は無かった。
   *
   * 同じ事故は `DeleteCaseDialog` でも起きており、 `index.tsx` が削除対象を
   * open 時点で snapshot して回避している (同ファイルのコメント参照)。 同じ
   * 規約に合わせる。 ずれていれば backend が ABORTED を返し、
   * `handleUpdateError` が「他のユーザーによって変更されました」 を出す。
   */
  const [draftBaseUpdatedAt, setDraftBaseUpdatedAt] = useState<
    Timestamp | undefined
  >(undefined)

  /** 編集モードで編集対象になっている値。 非編集時はサーバー値。 */
  const editingTags = draftTags ?? wfCase.tags

  const startEditingTags = (): void => {
    setDraftTags([...wfCase.tags])
    setDraftBaseUpdatedAt(wfCase.updatedAt)
    setIsEditingTags(true)
    setTagsError(undefined)
  }

  const stopEditingTags = (): void => {
    setIsEditingTags(false)
    setDraftTags(null)
    setDraftBaseUpdatedAt(undefined)
    // 削除予定は編集モードの中でしか確定できないので、 抜けるときに捨てる
    // (残すと次に開いたとき身に覚えのない削除予定が復活する)。
    setRemovedTagLabels([])
    setTagsError(undefined)
  }

  const handleUpdateError = (err: unknown): void => {
    // frontend の 20 個上限事前チェック (WorkflowCaseTagEditor) は同一
    // セッション内の複数追加やプール未取得中はすり抜けうる (design
    // tag-pool-limit-and-suggest)。backend の最終判定
    // (ResourceExhausted) を固定文言へ写像し、タグが再取得で消えるだけの
    // 無言 fail を防ぐ (AC-TAG-006)。
    if (err instanceof ConnectError && err.code === Code.ResourceExhausted) {
      setTagsError(t('common:tag.poolLimitError'))
      return
    }
    // 楽観ロック不一致 (= 編集中に他ユーザーが更新) と 編集不可 (完了・削除済) は
    // 「時間をおいて再試行」 では解決しないので、 unknown に丸めず個別の文言を出す
    // (同ページの SubStepCompleteDialog / ApprovalGateSection と同じ写像)。
    // 編集モード化で保存までの滞留が長くなり、 ABORTED の発生確率が上がった。
    if (err instanceof ConnectError && err.code === Code.Aborted) {
      setTagsError(t('page:workflowCaseDetail.error.aborted'))
      return
    }
    if (err instanceof ConnectError && err.code === Code.FailedPrecondition) {
      setTagsError(t('page:workflowCaseDetail.error.failedPrecondition'))
      return
    }
    setTagsError(t('page:workflowCaseDetail.error.unknown'))
  }

  /**
   * 下書きを 1 リクエストで確定する。 付与タグ (AC-TAG-003) と 「作成済のタグ」
   * の org 横断削除 (AC-TAG-009) を同時に送る。
   *
   * `UpdateWorkflowCaseRequest.tags` は full replace で FieldMask を持たないため、
   * 変更しないフィールド (targetDate) も毎回再送する
   * (target_date と同じ convention、 proto コメント参照)。
   */
  const saveTags = (): void => {
    setTagsError(undefined)
    updateAsync(
      create(UpdateWorkflowCaseRequestSchema, {
        id: wfCase.id,
        targetDate: wfCase.targetDate,
        // 下書きを取った時点の値。 live な `wfCase.updatedAt` を使うと
        // ロックが無効化される (`draftBaseUpdatedAt` の JSDoc 参照)。
        expectedUpdatedAt: draftBaseUpdatedAt ?? wfCase.updatedAt,
        tags: editingTags,
        removedTagLabels,
      }),
    )
      .then(() => {
        setIsConfirmingRemoval(false)
        stopEditingTags()
        // design `demand/case-detail.html` の `__cdEditSave()` と同じ文言・
        // 同じ閉じる (×) 付き。
        showSuccessToast({
          title: t('page:workflowCaseDetail.header.tagSavedToast'),
          closable: true,
        })
      })
      .catch((err: unknown) => {
        // 失敗時は編集モードのまま残す (下書きを捨てると入力が消える)。
        setIsConfirmingRemoval(false)
        handleUpdateError(err)
      })
  }

  /**
   * 「保存」 の押下口。 AC-TAG-009 Decision C は org 横断削除に**明示の確認**を
   * 求めるので、 削除予定がある時だけ確認ダイアログを挟んでから `saveTags` する
   * (旧実装は「削除を保存」 という別ボタンで確認していたが、 保存導線が
   * 「保存」 に一本化されたので確認もそこへ寄せる)。
   */
  const handleSaveClick = (): void => {
    if (removedTagLabels.length > 0) {
      setIsConfirmingRemoval(true)
      return
    }
    saveTags()
  }

  const sectionLabelKey = workflowCaseSectionLabelKey(wfCase.section)
  const sectionLabel = sectionLabelKey !== undefined ? t(sectionLabelKey) : '-'
  const areaLabel = convertAreaLabel(wfCase.areaCode)
  const targetDateLabel =
    wfCase.targetDate !== undefined
      ? formatDateDisplay(
          new Date(
            wfCase.targetDate.year,
            wfCase.targetDate.month - 1,
            wfCase.targetDate.day,
          ),
        )
      : '-'

  // design: h1 = 「計画断面 ・ エリア ・ 対象日」の分類軸を合成 (ケース名は spec #1515 で廃止)
  const titleParts = [sectionLabel, areaLabel, targetDateLabel].filter(
    (v) => v !== '' && v !== '-',
  )
  const title = titleParts.join(' ・ ')

  /**
   * 計画断面 / エリア / 対象日 は h1 が同じ値をそのまま並べているので、
   * **タイトルに出ている値は dl 側に出さない** (2026-08-28 デザイナー指摘
   * 「上部のエリアが広すぎるので下部のステップを多く表示できるように」)。
   * 3 項目まるごと重複しており、消しても失われる情報は無い。
   *
   * ただし h1 は値が `-` の軸を落とすので、**「タイトルに出たか」で判定する**
   * (無条件に消すと、エリア不明のケースでエリアがどこにも出なくなる)。
   * 3 軸すべてが `-` の時は title 自体が空でフォールバック文言になるため、
   * その時は 3 項目とも dl に残る。
   */
  const isInTitle = (value: string): boolean => titleParts.includes(value)

  return (
    <>
      <Box
        padding="20px"
        background="Surface.primary"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
      >
        {/* タイトル行。 design `.cd-title-row` は h1 の右端 (`margin-left: auto`)
            に編集導線を置く。 */}
        <HStack align="center" gap={2} marginBottom="12px">
          <Text textStyle="pageTitle" color="Text.highEmphasis">
            {title !== '' ? title : t('page:workflowCaseDetail.title')}
          </Text>
          {/* 変更不可 (完了・削除済) のケースでは出さない — 押しても編集できる
              ものが無いため。 編集中は キャンセル (tertiary) + 保存 (primary) の
              対に差し替える (design の編集 modal footer と同じ並び・同じ variant。
              2026-08-28 デザイナー指示)。 */}
          {!isReadonly &&
            (isTagEditing ? (
              <HStack gap={2} marginLeft="auto">
                <TertiaryButton
                  disabled={isUpdating}
                  onClick={stopEditingTags}
                  size="sm"
                >
                  {t('common:action.cancel')}
                </TertiaryButton>
                <PrimaryButton
                  disabled={isUpdating}
                  onClick={handleSaveClick}
                  size="sm"
                >
                  {t('common:action.save')}
                </PrimaryButton>
              </HStack>
            ) : (
              <TertiaryButton
                marginLeft="auto"
                onClick={startEditingTags}
                size="sm"
              >
                <FaPenToSquareRegular />
                {t('page:workflowCaseDetail.header.tagEditStart')}
              </TertiaryButton>
            ))}
        </HStack>

        {/* 列間は design (`.cd-meta-grid` の `gap: var(--space-6)`) どおり 24px。
            **行間だけ 12px に詰める** — 折り返した時の行間 24px は 1 行ぶんに近い
            高さを空けており、項目数が多いこの dl では折り返すたびに効いてくる
            (2026-08-28 デザイナー指摘)。列方向は詰めると項目の切れ目が読めなくなる
            ので触らない。 */}
        <chakra.dl
          flexWrap="wrap"
          gap="12px 24px"
          display="flex"
          margin={0}
          padding={0}
        >
          <HeaderMetaItem
            label={t('page:workflowCaseDetail.header.workflowId')}
          >
            <Text
              color="Text.lowEmphasis"
              fontSize="md"
              fontVariantNumeric="tabular-nums"
            >
              {wfCase.workflowId !== ''
                ? wfCase.workflowId
                : wfCase.id.slice(0, 8).toUpperCase()}
            </Text>
          </HeaderMetaItem>

          <HeaderMetaItem label={t('page:workflowCaseDetail.header.tags')}>
            <Box flexDirection="column" gap="4px" display="flex" width="100%">
              {isTagEditing ? (
                /* 編集中は下書き (draftTags) を編集する。 サーバーへ送るのは
                   「保存」 の 1 回だけ (`saveTags`)。 */
                <WorkflowCaseTagEditor
                  disabled={isReadonly || isUpdating}
                  onChange={setDraftTags}
                  onRemovedTagLabelsChange={setRemovedTagLabels}
                  removedTagLabels={removedTagLabels}
                  suggestedTags={tagPoolData?.entries}
                  tags={editingTags}
                />
              ) : (
                /* 既定は付与タグの読み取り専用表示 (design `.cd-meta-item` の
                   `dd.tag-list`)。 折りたたまずに全件出す — 一覧の
                   `WorkflowCaseTagList` が先頭 2 個 + 「+N」 にするのは行の高さを
                   揃えるためで、 ヘッダーにその制約は無い。 */
                <HStack flexWrap="wrap" gap={1}>
                  {wfCase.tags.length > 0 ? (
                    wfCase.tags.map((tag) => (
                      <WorkflowCaseTagChip
                        key={tag.label}
                        color={tag.color}
                        label={tag.label}
                      />
                    ))
                  ) : (
                    <Text as="span" color="Text.midEmphasis">
                      {WORKFLOW_CASE_NO_VALUE_LABEL}
                    </Text>
                  )}
                </HStack>
              )}
              {/* AC-TAG-009 Decision C の「明示確定」 は保存導線に統合した。
                  以前は削除予定がある時だけ「削除を保存」 という別ボタンを出して
                  いたが、 保存が「保存」 に一本化された以上ボタンが 2 つ並ぶと
                  どちらが確定なのか分からない。 確認ダイアログ (下の
                  ConfirmDialog) は残すので、 org 横断削除が確認なしで走ることは
                  ない。 */}
              {tagsError !== undefined && (
                <CompactAlert role="alert" status="error" title={tagsError} />
              )}
            </Box>
          </HeaderMetaItem>

          {!isInTitle(sectionLabel) && (
            <HeaderMetaItem label={t('page:workflowCaseDetail.header.section')}>
              <Text color="Text.highEmphasis" fontSize="md">
                {sectionLabel}
              </Text>
            </HeaderMetaItem>
          )}

          {!isInTitle(areaLabel) && (
            <HeaderMetaItem label={t('page:workflowCaseDetail.header.area')}>
              <Text color="Text.highEmphasis" fontSize="md">
                {/* issue #5219: BG 不要ケースは area_code が UNSPECIFIED で
                    返る。areaLabelOrDash が空欄でなく「—」に落とす。 */}
                {areaLabelOrDash(wfCase.areaCode)}
              </Text>
            </HeaderMetaItem>
          )}

          <HeaderMetaItem label={t('page:workflowCaseDetail.header.bg')}>
            <Text color="Text.highEmphasis" fontSize="md">
              {/* AC-11: BG 軸を持たないケースは空欄でなく「—」を出す。 */}
              {wfCase.bgCode ?? WORKFLOW_CASE_BG_ABSENT_LABEL}
            </Text>
          </HeaderMetaItem>

          {!isInTitle(targetDateLabel) && (
            <HeaderMetaItem
              label={t('page:workflowCaseDetail.header.targetDate')}
            >
              <Text color="Text.highEmphasis" fontSize="md">
                {targetDateLabel}
              </Text>
            </HeaderMetaItem>
          )}

          <HeaderMetaItem label={t('page:workflowCaseDetail.header.template')}>
            <Text color="Text.highEmphasis" fontSize="md">
              {localizeBuiltinWorkflowText(wfCase.workflowTemplateName, t)}
            </Text>
          </HeaderMetaItem>

          <HeaderMetaItem label={t('page:workflowCaseDetail.header.createdAt')}>
            <Text color="Text.highEmphasis" fontSize="md">
              {wfCase.createdAt !== undefined
                ? formatDateTimeFull(timestampDate(wfCase.createdAt))
                : '-'}
            </Text>
          </HeaderMetaItem>

          <HeaderMetaItem label={t('page:workflowCaseDetail.header.status')}>
            <WorkflowCaseStatusBadge status={wfCase.status} />
          </HeaderMetaItem>

          {/* 終端の記録 (いつ・誰が)。 完了・削除のバナーには出さず、 ここに置く
              (2026-10-20 デザイナー指示: Alert に個別の値を並べない。 持ち場の無い値は
              Alert に残すより先に持ち場を作る。 UI 辞書 components/alert-placement.md)。
              実施者は表示名に解決し、 解決できない時も生の member ID (auth0|...) は出さない */}
          {terminalRecord !== undefined && (
            <>
              <HeaderMetaItem label={t(terminalRecord.atLabelKey)}>
                <Text color="Text.highEmphasis" fontSize="md">
                  {terminalRecord.at !== undefined
                    ? formatDateTimeFull(timestampDate(terminalRecord.at))
                    : '-'}
                </Text>
              </HeaderMetaItem>
              <HeaderMetaItem label={t(terminalRecord.byLabelKey)}>
                <Text color="Text.highEmphasis" fontSize="md">
                  {terminalRecord.by !== undefined
                    ? (resolveDisplayName(terminalRecord.by) ?? '-')
                    : '-'}
                </Text>
              </HeaderMetaItem>
            </>
          )}

          <HeaderMetaItem label={t('page:workflowCaseDetail.header.progress')}>
            <StepProgressBar done={doneSubSteps} total={totalSubSteps} />
          </HeaderMetaItem>
        </chakra.dl>
      </Box>
      <ConfirmDialog
        body={t('common:tag.confirmRemovalBody', {
          count: removedTagLabels.length,
          // 区切りは言語で違う (ja は無し、 en は ", ") ので、 2 つ目以降の
          // タグ名の書式に区切りを含める。
          labels: removedTagLabels
            .map((label, i) =>
              t(
                i === 0
                  ? 'common:tag.confirmRemovalLabel'
                  : 'common:tag.confirmRemovalLabelNext',
                { label: localizeBuiltinWorkflowText(label, t) },
              ),
            )
            .join(''),
        })}
        confirmLabel={t('common:tag.confirmRemovalButton')}
        destructive
        isLoading={isUpdating}
        isOpen={isConfirmingRemoval}
        onCancel={() => {
          // AC-TAG-009 Decision C (Design Note Verification「キャンセルで
          // 破棄」): 確認をキャンセルすると削除予定は破棄され、タグ自体は
          // 残存する（明示確定を経ない限り org 横断削除は送出されない）。
          setIsConfirmingRemoval(false)
          setRemovedTagLabels([])
        }}
        onConfirm={saveTags}
        title={t('common:tag.confirmRemovalTitle', {
          count: removedTagLabels.length,
        })}
      />
    </>
  )
}

const HeaderMetaItem = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): React.ReactElement => (
  <Box flexDirection="column" gap="2px" display="flex">
    <Text as="dt" textStyle="label" margin={0} color="Text.highEmphasis">
      {label}
    </Text>
    <Text
      as="dd"
      alignItems="center"
      flexWrap="wrap"
      gap="4px"
      display="flex"
      margin={0}
      color="Text.highEmphasis"
    >
      {children}
    </Text>
  </Box>
)
